import { Dispatch } from 'redux';
import { getPayers, createPayer, updatePayer, deletePayer } from '../../services/api';
import { GetPayersRequest, CreatePayerRequest, UpdatePayerRequest, PayerData, PaginationData } from '../../services/types';

// Action Types
export const FETCH_PAYERS_REQUEST = 'FETCH_PAYERS_REQUEST';
export const FETCH_PAYERS_SUCCESS = 'FETCH_PAYERS_SUCCESS';
export const FETCH_PAYERS_FAILURE = 'FETCH_PAYERS_FAILURE';

export const CREATE_PAYER_REQUEST = 'CREATE_PAYER_REQUEST';
export const CREATE_PAYER_SUCCESS = 'CREATE_PAYER_SUCCESS';
export const CREATE_PAYER_FAILURE = 'CREATE_PAYER_FAILURE';

export const UPDATE_PAYER_REQUEST = 'UPDATE_PAYER_REQUEST';
export const UPDATE_PAYER_SUCCESS = 'UPDATE_PAYER_SUCCESS';
export const UPDATE_PAYER_FAILURE = 'UPDATE_PAYER_FAILURE';

export const DELETE_PAYER_REQUEST = 'DELETE_PAYER_REQUEST';
export const DELETE_PAYER_SUCCESS = 'DELETE_PAYER_SUCCESS';
export const DELETE_PAYER_FAILURE = 'DELETE_PAYER_FAILURE';

export const SET_PAYER_SEARCH_TERM = 'SET_PAYER_SEARCH_TERM';
export const SET_PAYER_CURRENT_PAGE = 'SET_PAYER_CURRENT_PAGE';

// Action Interfaces
export interface FetchPayersRequestAction {
  type: typeof FETCH_PAYERS_REQUEST;
}

export interface FetchPayersSuccessAction {
  type: typeof FETCH_PAYERS_SUCCESS;
  payload: {
    payers: PayerData[];
    pagination: PaginationData;
  };
}

export interface FetchPayersFailureAction {
  type: typeof FETCH_PAYERS_FAILURE;
  payload: string;
}

export interface CreatePayerRequestAction {
  type: typeof CREATE_PAYER_REQUEST;
}

export interface CreatePayerSuccessAction {
  type: typeof CREATE_PAYER_SUCCESS;
  payload: PayerData;
}

export interface CreatePayerFailureAction {
  type: typeof CREATE_PAYER_FAILURE;
  payload: string;
}

export interface UpdatePayerRequestAction {
  type: typeof UPDATE_PAYER_REQUEST;
}

export interface UpdatePayerSuccessAction {
  type: typeof UPDATE_PAYER_SUCCESS;
  payload: PayerData;
}

export interface UpdatePayerFailureAction {
  type: typeof UPDATE_PAYER_FAILURE;
  payload: string;
}

export interface DeletePayerRequestAction {
  type: typeof DELETE_PAYER_REQUEST;
}

export interface DeletePayerSuccessAction {
  type: typeof DELETE_PAYER_SUCCESS;
  payload: string; // payer ID
}

export interface DeletePayerFailureAction {
  type: typeof DELETE_PAYER_FAILURE;
  payload: string;
}

export interface SetPayerSearchTermAction {
  type: typeof SET_PAYER_SEARCH_TERM;
  payload: string;
}

export interface SetPayerCurrentPageAction {
  type: typeof SET_PAYER_CURRENT_PAGE;
  payload: number;
}

export type PayerActionTypes =
  | FetchPayersRequestAction
  | FetchPayersSuccessAction
  | FetchPayersFailureAction
  | CreatePayerRequestAction
  | CreatePayerSuccessAction
  | CreatePayerFailureAction
  | UpdatePayerRequestAction
  | UpdatePayerSuccessAction
  | UpdatePayerFailureAction
  | DeletePayerRequestAction
  | DeletePayerSuccessAction
  | DeletePayerFailureAction
  | SetPayerSearchTermAction
  | SetPayerCurrentPageAction;

// Action Creators
export const fetchPayersRequest = (): FetchPayersRequestAction => ({
  type: FETCH_PAYERS_REQUEST,
});

export const fetchPayersSuccess = (
  payers: PayerData[],
  pagination: PaginationData
): FetchPayersSuccessAction => ({
  type: FETCH_PAYERS_SUCCESS,
  payload: { payers, pagination },
});

export const fetchPayersFailure = (error: string): FetchPayersFailureAction => ({
  type: FETCH_PAYERS_FAILURE,
  payload: error,
});

export const createPayerRequest = (): CreatePayerRequestAction => ({
  type: CREATE_PAYER_REQUEST,
});

export const createPayerSuccess = (payer: PayerData): CreatePayerSuccessAction => ({
  type: CREATE_PAYER_SUCCESS,
  payload: payer,
});

export const createPayerFailure = (error: string): CreatePayerFailureAction => ({
  type: CREATE_PAYER_FAILURE,
  payload: error,
});

export const updatePayerRequest = (): UpdatePayerRequestAction => ({
  type: UPDATE_PAYER_REQUEST,
});

export const updatePayerSuccess = (payer: PayerData): UpdatePayerSuccessAction => ({
  type: UPDATE_PAYER_SUCCESS,
  payload: payer,
});

export const updatePayerFailure = (error: string): UpdatePayerFailureAction => ({
  type: UPDATE_PAYER_FAILURE,
  payload: error,
});

export const deletePayerRequest = (): DeletePayerRequestAction => ({
  type: DELETE_PAYER_REQUEST,
});

export const deletePayerSuccess = (payerId: string): DeletePayerSuccessAction => ({
  type: DELETE_PAYER_SUCCESS,
  payload: payerId,
});

export const deletePayerFailure = (error: string): DeletePayerFailureAction => ({
  type: DELETE_PAYER_FAILURE,
  payload: error,
});

export const setPayerSearchTerm = (searchTerm: string): SetPayerSearchTermAction => ({
  type: SET_PAYER_SEARCH_TERM,
  payload: searchTerm,
});

export const setPayerCurrentPage = (page: number): SetPayerCurrentPageAction => ({
  type: SET_PAYER_CURRENT_PAGE,
  payload: page,
});

// Thunk Actions
export const fetchPayers = (params?: GetPayersRequest) => {
  return async (dispatch: Dispatch<PayerActionTypes>) => {
    dispatch(fetchPayersRequest());
    try {
      const response = await getPayers(params);
      if (response.success) {
        dispatch(fetchPayersSuccess(response.data, response.pagination));
      } else {
        dispatch(fetchPayersFailure(response.message || 'Failed to fetch payers'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch payers';
      dispatch(fetchPayersFailure(errorMessage));
    }
  };
};

export const addPayer = (data: CreatePayerRequest) => {
  return async (dispatch: Dispatch<PayerActionTypes>) => {
    dispatch(createPayerRequest());
    try {
      const response = await createPayer(data);
      if (response.success) {
        dispatch(createPayerSuccess(response.data));
        return response;
      } else {
        dispatch(createPayerFailure(response.message || 'Failed to create payer'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create payer';
      dispatch(createPayerFailure(errorMessage));
      throw error;
    }
  };
};

export const editPayer = (payerId: string, data: UpdatePayerRequest) => {
  return async (dispatch: Dispatch<PayerActionTypes>) => {
    dispatch(updatePayerRequest());
    try {
      const response = await updatePayer(payerId, data);
      if (response.success) {
        dispatch(updatePayerSuccess(response.data));
        return response;
      } else {
        dispatch(updatePayerFailure(response.message || 'Failed to update payer'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update payer';
      dispatch(updatePayerFailure(errorMessage));
      throw error;
    }
  };
};

export const removePayer = (payerId: string) => {
  return async (dispatch: Dispatch<PayerActionTypes>) => {
    dispatch(deletePayerRequest());
    try {
      const response = await deletePayer(payerId);
      if (response.success) {
        dispatch(deletePayerSuccess(payerId));
        return response;
      } else {
        dispatch(deletePayerFailure(response.message || 'Failed to delete payer'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete payer';
      dispatch(deletePayerFailure(errorMessage));
      throw error;
    }
  };
};

